import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import './SMTPSettings.css';

interface SMTPConfig {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  password: string;
  fromEmail: string;
  fromName: string;
}

const SMTPSettings: React.FC = () => {
  const [config, setConfig] = useState<SMTPConfig>({
    host: '',
    port: 587,
    secure: false,
    user: '',
    password: '',
    fromEmail: '',
    fromName: 'Lakics Márk - Szobafestő'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchConfig();
  }, [navigate]);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/smtp/config', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data && Object.keys(data).length > 0) {
          setConfig({
            host: data.host || '',
            port: data.port || 587,
            secure: data.secure || false,
            user: data.user || '',
            password: data.password || '',
            fromEmail: data.fromEmail || '',
            fromName: data.fromName || 'Lakics Márk - Szobafestő'
          });
        }
      } else if (response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        setError('Failed to fetch SMTP configuration');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/smtp/config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        setSuccess('SMTP configuration saved successfully');
      } else if (response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save configuration');
      }
    } catch (err) {
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/smtp/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        setSuccess('Test email sent successfully! Check your inbox.');
      } else if (response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to send test email');
      }
    } catch (err) {
      setError('Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  if (loading) {
    return (
      <AdminLayout title="SMTP Settings">
        <div className="loading">Loading...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="SMTP Settings">
      <div className="smtp-settings-content">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="settings-section">
          <h2>Email Configuration</h2>
          <p>Configure SMTP settings for the contact form emails.</p>
          
          <form onSubmit={handleSave} className="smtp-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="host">SMTP Host</label>
                <input
                  type="text"
                  id="host"
                  value={config.host}
                  onChange={(e) => setConfig({...config, host: e.target.value})}
                  placeholder="smtp.gmail.com"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="port">Port</label>
                <input
                  type="number"
                  id="port"
                  value={config.port}
                  onChange={(e) => setConfig({...config, port: parseInt(e.target.value)})}
                  placeholder="587"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={config.secure}
                  onChange={(e) => setConfig({...config, secure: e.target.checked})}
                />
                Use SSL/TLS (secure connection)
              </label>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="user">Email/Username</label>
                <input
                  type="email"
                  id="user"
                  value={config.user}
                  onChange={(e) => setConfig({...config, user: e.target.value})}
                  placeholder="your-email@gmail.com"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="password">Password/App Password</label>
                <input
                  type="password"
                  id="password"
                  value={config.password}
                  onChange={(e) => setConfig({...config, password: e.target.value})}
                  placeholder="Your email password or app password"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="fromEmail">From Email</label>
                <input
                  type="email"
                  id="fromEmail"
                  value={config.fromEmail}
                  onChange={(e) => setConfig({...config, fromEmail: e.target.value})}
                  placeholder="noreply@yourdomain.com"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="fromName">From Name</label>
                <input
                  type="text"
                  id="fromName"
                  value={config.fromName}
                  onChange={(e) => setConfig({...config, fromName: e.target.value})}
                  placeholder="Lakics Márk - Szobafestő"
                  required
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="save-btn" disabled={saving}>
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
              <button type="button" onClick={handleTest} className="test-btn" disabled={testing}>
                {testing ? 'Sending...' : 'Send Test Email'}
              </button>
            </div>
          </form>

          <div className="info-box">
            <h3>Common SMTP Settings:</h3>
            <div className="smtp-providers">
              <div className="provider">
                <strong>Gmail:</strong> smtp.gmail.com:587 (use App Password)
              </div>
              <div className="provider">
                <strong>Outlook:</strong> smtp-mail.outlook.com:587
              </div>
              <div className="provider">
                <strong>Yahoo:</strong> smtp.mail.yahoo.com:587
              </div>
            </div>
            <p><strong>Note:</strong> For Gmail, enable 2FA and use an App Password instead of your regular password.</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SMTPSettings;
